import { useEffect, useRef, useState } from 'react';

/**
 * Hook to enable pull-to-refresh functionality
 * Only triggers when user is already at the top of the page
 * @param {Function} onRefresh - Callback function to execute on refresh
 * @param {Object} options - Configuration options
 * @param {number} options.threshold - Distance in pixels to trigger refresh (default: 80)
 * @param {boolean} options.enabled - Whether pull-to-refresh is enabled (default: true)
 */
export const usePullToRefresh = (onRefresh, options = {}) => {
  const { threshold = 80, enabled = true } = options;
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const startY = useRef(0);
  const currentY = useRef(0);
  const isPulling = useRef(false);
  const pullDistanceRef = useRef(0);

  useEffect(() => {
    if (!enabled || !onRefresh) return;

    const handleTouchStart = (e) => {
      // Only trigger if we're at the top of the page
      if (window.scrollY === 0 && window.pageYOffset === 0) {
        startY.current = e.touches[0].clientY;
        isPulling.current = true;
      }
    };

    const handleTouchMove = (e) => {
      if (!isPulling.current) return;

      currentY.current = e.touches[0].clientY;
      const distance = currentY.current - startY.current;

      // Only allow pulling down (positive distance)
      if (distance > 0 && window.scrollY === 0) {
        // Prevent default scrolling behavior
        e.preventDefault();
        const clampedDistance = Math.min(distance, threshold * 1.5);
        pullDistanceRef.current = clampedDistance;
        setPullDistance(clampedDistance); // Allow slight overscroll for visual feedback
      } else {
        isPulling.current = false;
        pullDistanceRef.current = 0;
        setPullDistance(0);
      }
    };

    const handleTouchEnd = () => {
      if (!isPulling.current) return;

      const finalDistance = pullDistanceRef.current;
      isPulling.current = false;
      pullDistanceRef.current = 0;
      setPullDistance(0);

      // If pulled far enough, trigger refresh
      if (finalDistance >= threshold && window.scrollY === 0) {
        setIsRefreshing(true);
        
        // Call the refresh callback
        Promise.resolve(onRefresh()).finally(() => {
          setIsRefreshing(false);
        });
      }
    };

    // Add event listeners
    document.addEventListener('touchstart', handleTouchStart, { passive: false });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enabled, onRefresh, threshold]);

  return {
    isRefreshing,
    pullDistance,
    pullProgress: Math.min(pullDistance / threshold, 1),
  };
};

